import * as THREE from 'three';

export type LightState = 'on' | 'off' | 'dying' | 'blink' | 'broken' | 'sensorOff' | 'sensorOn' | 'candle';

export type LightSourceOptions = {
  id: string;
  lamp: THREE.Vector3;
  ground: THREE.Vector2;
  radius: number;
  color: THREE.ColorRepresentation;
  intensity: number;
  state?: LightState;
  /** Light this source contributes to the "is she lit" check (0..1). */
  exposure?: number;
  pool?: boolean;
  cone?: boolean;
  coneHeight?: number;
};

export class LightSource {
  readonly id: string;
  readonly lamp: THREE.Vector3;
  readonly ground: THREE.Vector2;
  readonly radius: number;
  readonly color: THREE.Color;
  readonly intensity: number;
  readonly exposure: number;
  readonly initialState: LightState;
  state: LightState;
  level = 1;
  /** `level` before any stutter: what counts for "is she lit" (a passing flicker is only a scare). */
  steadyLevel = 1;
  stateTime = 0;
  /** Seconds left of a stutter (something passing under it); the lamp then carries on as before. */
  private disturbed = 0;
  /** Emissive materials whose brightness follows `level`. */
  readonly glowMaterials: THREE.MeshBasicMaterial[] = [];
  readonly glowBase: THREE.Color[] = [];
  pool: THREE.Mesh | null = null;
  cone: THREE.Mesh | null = null;
  /** Soft glow around the lamp: light scattering in the night air. */
  halo: THREE.Sprite | null = null;
  private seed: number;

  constructor(options: LightSourceOptions) {
    this.id = options.id;
    this.lamp = options.lamp.clone();
    this.ground = options.ground.clone();
    this.radius = options.radius;
    this.color = new THREE.Color(options.color);
    this.intensity = options.intensity;
    this.exposure = options.exposure ?? 1;
    this.initialState = options.state ?? 'on';
    this.state = this.initialState;
    this.seed = (this.lamp.x * 13.17 + this.lamp.z * 7.31) % 97;
  }

  addGlow(material: THREE.MeshBasicMaterial): void {
    this.glowMaterials.push(material);
    this.glowBase.push(material.color.clone());
  }

  setState(state: LightState): void {
    if (this.state === state) return;
    this.state = state;
    this.stateTime = 0;
  }

  reset(): void {
    this.state = this.initialState;
    this.stateTime = 0;
    this.disturbed = 0;
  }

  /** Stutter for `seconds`, then go back to whatever it was doing. */
  disturb(seconds: number): void {
    this.disturbed = Math.max(this.disturbed, seconds);
  }

  update(dt: number, time: number): void {
    this.stateTime += dt;
    const t = this.stateTime;
    switch (this.state) {
      case 'on':
        this.level = 1;
        break;
      case 'off':
      case 'sensorOff':
        this.level = 0;
        break;
      case 'sensorOn':
        // Snap on with a short electrical stutter.
        this.level = t < 0.08 ? 1 : t < 0.16 ? 0.2 : 1;
        break;
      case 'candle': {
        // Soft, uneven flame; fades in when first lit.
        const n = Math.sin(time * 7.3 + this.seed) * 0.5 + Math.sin(time * 13.1 + this.seed * 2) * 0.3;
        this.level = Math.min(1, t / 1.5) * (0.82 + n * 0.12);
        break;
      }
      case 'blink':
        this.level = Math.sin(time * 5.5 + this.seed) > 0 ? 1 : 0.18;
        break;
      case 'broken': {
        const n = hash(Math.floor(time * 9 + this.seed));
        this.level = n > 0.92 ? 0.55 : n > 0.85 ? 0.25 : 0.04;
        break;
      }
      case 'dying': {
        // ~2.6 s of increasingly sick flicker, then dark for good.
        if (t > 2.6) {
          this.state = 'off';
          this.level = 0;
          break;
        }
        const rate = 7 + t * 9;
        const n = hash(Math.floor(time * rate + this.seed));
        const threshold = 0.2 + t * 0.28;
        this.level = n > threshold ? 1 - t * 0.25 : 0.05;
        break;
      }
    }
    this.steadyLevel = this.level;
    if (this.disturbed > 0) {
      this.disturbed -= dt;
      const n = hash(Math.floor(time * 14 + this.seed * 3));
      this.level *= n > 0.55 ? 1 : n > 0.3 ? 0.3 : 0.04;
    }
    this.applyVisuals();
  }

  private applyVisuals(): void {
    const lvl = this.level;
    for (let i = 0; i < this.glowMaterials.length; i += 1) {
      this.glowMaterials[i].color.copy(this.glowBase[i]).multiplyScalar(0.08 + lvl * 0.92);
    }
    if (this.pool) {
      (this.pool.material as THREE.MeshBasicMaterial).opacity = lvl * 0.42;
      this.pool.visible = lvl > 0.01;
    }
    if (this.cone) {
      (this.cone.material as THREE.ShaderMaterial).uniforms.uOpacity.value = lvl * 0.16;
      this.cone.visible = lvl > 0.01;
    }
    if (this.halo) {
      this.halo.material.opacity = lvl * 0.35;
      this.halo.visible = lvl > 0.01;
    }
  }
}

function hash(n: number): number {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

const POOL_SIZE = 10;

/**
 * Owns every light source in the neighbourhood. Only the nearest few get a real
 * THREE.PointLight (a fixed-size pool, so shader light counts never change);
 * the rest are carried by glowing lamp heads, ground pools and light cones.
 */
export class LightSystem {
  readonly sources: LightSource[] = [];
  readonly group = new THREE.Group();
  /**
   * Additive light effects (pools, beams, halos). Kept in their own group so
   * the ambient-occlusion pass can hide them: they aren't solid surfaces.
   */
  readonly fx = new THREE.Group();
  private readonly pointLights: THREE.PointLight[] = [];
  private readonly poolTexture: THREE.Texture;
  private readonly haloTexture: THREE.Texture;
  private readonly sorted: LightSource[] = [];
  private readonly coneGeometry = new THREE.ConeGeometry(1, 1, 24, 1, true);
  private readonly poolGeometry = new THREE.PlaneGeometry(1, 1);

  constructor() {
    this.poolTexture = createRadialTexture();
    this.haloTexture = createHaloTexture();
    this.group.add(this.fx);
    for (let i = 0; i < POOL_SIZE; i += 1) {
      const light = new THREE.PointLight('#ffffff', 0, 20, 1.6);
      light.castShadow = false;
      this.pointLights.push(light);
      this.group.add(light);
    }
  }

  add(options: LightSourceOptions): LightSource {
    const source = new LightSource(options);
    if (options.pool !== false) {
      const material = new THREE.MeshBasicMaterial({
        map: this.poolTexture,
        color: source.color,
        transparent: true,
        opacity: 0.5,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -2,
      });
      const pool = new THREE.Mesh(this.poolGeometry, material);
      pool.rotation.x = -Math.PI / 2;
      pool.scale.setScalar(source.radius * 2.1);
      pool.position.set(source.ground.x, 0.03, source.ground.y);
      pool.renderOrder = 2;
      source.pool = pool;
      this.fx.add(pool);
    }
    if (options.cone) {
      const h = options.coneHeight ?? source.lamp.y;
      const cone = new THREE.Mesh(this.coneGeometry, createBeamMaterial(source.color));
      cone.scale.set(source.radius * 0.5, h, source.radius * 0.5);
      cone.position.set(source.lamp.x, source.lamp.y - h / 2, source.lamp.z);
      cone.renderOrder = 3;
      source.cone = cone;
      this.fx.add(cone);
    }
    // Every light gets a halo; bigger lights scatter more.
    const halo = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.haloTexture,
        color: source.color,
        transparent: true,
        opacity: 0.5,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        fog: true,
      }),
    );
    halo.scale.setScalar(THREE.MathUtils.clamp(source.radius * 0.5, 1.2, 5.5) * Math.min(1.6, Math.sqrt(source.intensity / 20)));
    halo.position.copy(source.lamp);
    halo.renderOrder = 4;
    source.halo = halo;
    this.fx.add(halo);
    this.sources.push(source);
    return source;
  }

  get(id: string): LightSource | undefined {
    return this.sources.find((s) => s.id === id);
  }

  reset(): void {
    for (const s of this.sources) s.reset();
  }

  update(dt: number, time: number, focus: THREE.Vector3): void {
    for (const s of this.sources) s.update(dt, time);

    this.sorted.length = 0;
    for (const s of this.sources) if (s.level > 0.01) this.sorted.push(s);
    const fx = focus.x;
    const fz = focus.z;
    this.sorted.sort(
      (a, b) =>
        (a.lamp.x - fx) ** 2 + (a.lamp.z - fz) ** 2 - ((b.lamp.x - fx) ** 2 + (b.lamp.z - fz) ** 2),
    );
    for (let i = 0; i < this.pointLights.length; i += 1) {
      const light = this.pointLights[i];
      const s = this.sorted[i];
      if (!s) {
        light.intensity = 0;
        continue;
      }
      light.position.copy(s.lamp);
      light.color.copy(s.color);
      light.distance = s.radius * 2.2;
      // Fade out the farthest pooled lights so reassignment never pops.
      const rankFade = i < POOL_SIZE - 3 ? 1 : (POOL_SIZE - i) / 4;
      light.intensity = s.intensity * s.level * rankFade * 0.55;
    }
  }

  /** How lit a ground point is (0 = black, ~1 = standing under a lamp). */
  lightLevelAt(x: number, z: number, ignore?: LightSource): number {
    let total = 0;
    for (const s of this.sources) {
      if (s === ignore || s.steadyLevel <= 0.01 || s.exposure <= 0) continue;
      const d = Math.hypot(s.ground.x - x, s.ground.y - z);
      if (d >= s.radius) continue;
      const f = 1 - d / s.radius;
      total += f * Math.sqrt(f) * s.steadyLevel * s.exposure;
    }
    return Math.min(1.5, total);
  }

  dispose(): void {
    this.poolTexture.dispose();
    this.haloTexture.dispose();
    this.coneGeometry.dispose();
    this.poolGeometry.dispose();
    for (const s of this.sources) {
      (s.pool?.material as THREE.Material | undefined)?.dispose();
      (s.cone?.material as THREE.Material | undefined)?.dispose();
      s.halo?.material.dispose();
    }
  }
}

/**
 * A soft volumetric beam: brightest at the lamp, fading toward the ground and
 * at the silhouette edges, and swallowed by fog with distance (additive, so it
 * fades to nothing rather than to the fog colour).
 */
function createBeamMaterial(color: THREE.Color): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog),
      uColor: { value: color.clone() },
      uOpacity: { value: 0.16 },
    },
    vertexShader: /* glsl */ `
      #include <fog_pars_vertex>
      varying float vHeight;
      varying vec3 vNormalV;
      varying vec3 vViewDir;
      void main() {
        vHeight = position.y + 0.5;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vNormalV = normalize(normalMatrix * normal);
        vViewDir = normalize(-mvPosition.xyz);
        gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }
    `,
    fragmentShader: /* glsl */ `
      #include <fog_pars_fragment>
      uniform vec3 uColor;
      uniform float uOpacity;
      varying float vHeight;
      varying vec3 vNormalV;
      varying vec3 vViewDir;
      void main() {
        float facing = abs(dot(normalize(vNormalV), normalize(vViewDir)));
        float edge = pow(facing, 1.6);
        float fall = mix(0.18, 1.0, pow(vHeight, 1.4));
        float a = uOpacity * edge * fall;
        #ifdef USE_FOG
          #ifdef FOG_EXP2
            float fogF = 1.0 - exp(-fogDensity * fogDensity * vFogDepth * vFogDepth);
          #else
            float fogF = smoothstep(fogNear, fogFar, vFogDepth);
          #endif
          a *= 1.0 - fogF;
        #endif
        gl_FragColor = vec4(uColor * a, a);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
    fog: true,
  });
}

/** Halo falloff: a hot core and a long soft tail, like glare in damp air. */
function createHaloTexture(): THREE.CanvasTexture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.08, 'rgba(255,255,255,0.75)');
  g.addColorStop(0.25, 'rgba(255,255,255,0.25)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.07)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function createRadialTexture(): THREE.CanvasTexture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,0.9)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.45)');
  g.addColorStop(0.7, 'rgba(255,255,255,0.1)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
